/**
 * Filters undefined, null or empty strings as well as boolean false from a list of comma separated items, joins them and returns a string
 * Returns null if 'classes' is empty
 * @param {object} classes - An arguments object
 */
export const setClassNames = (...classes) => {
	return [...classes].filter((className) => className).join(' ') || null;
};

export function formatLocationData(
	requestDataAccountId,
	vehicleAccountId,
	vehicleStatus
) {
	// logic to determine what vehicle status should be
	let status = 'ON_LOT';
	let accountId = requestDataAccountId;

	if (vehicleStatus.toUpperCase() === 'IN_TRANSIT') {
		status = 'IN_TRANSIT';
	} else if (vehicleStatus.toUpperCase() === 'ON_DEPOSIT') {
		status = 'RESERVED';
	} else if (vehicleStatus.toUpperCase() === 'IN_TRANSIT_AT_PORT') {
		status = 'IN_TRANSIT_AT_PORT';
	} else if (vehicleStatus.toUpperCase() === 'IN_TRANSIT_AT_FACTORY') {
		status = 'IN_TRANSIT_AT_FACTORY';
	} else if (vehicleStatus.toUpperCase() === 'IN_TRANSIT_SOLD') {
		status = 'IN_TRANSIT_SOLD';
	} else if (requestDataAccountId !== vehicleAccountId) {
		status = 'OFF_LOT';
		accountId = vehicleAccountId;
	}

	const locationObj = {
		status,
		accountId,
		name: ''
	};
	return locationObj;
}

/**
 * Receives link prop
 * returns object of link properties to render in widget
 * @param label
 * @param labels
 * @param sitemap
 * @param widgetInfo
 */

const getLink = (
	linkOpt,
	labels,
	sitemap,
	{ vehicleId, vehicleCategory, pageAlias, selectedAccountId, uri },
	enableLinkToChildVDP
) => {
	const defaultLinkObj = {
		displayLink: true,
		linkLabel: '',
		linkClass: 'dialog mr-3 pr-2',
		dataWidth: null,
		href: null,
		dataHref: null,
		dataTitle: null,
		relData: null
	};

	switch (linkOpt) {
		case 'HOURS_AND_DIRECTIONS': {
			const pagePathParam = enableLinkToChildVDP
				? `&pagePath=${encodeURIComponent(uri)}`
				: '';
			return {
				...defaultLinkObj,
				linkLabel: labels.get('LOCATION_DETAILS'),
				dataWidth: 700,
				href: `${sitemap.get(
					'HOURS_DIRECTIONS_FRAGMENT'
				)}?selectedAccountId=${selectedAccountId}${pagePathParam}`,
				dataHref: `${sitemap.get(
					'HOURS_DIRECTIONS_FRAGMENT'
				)}?selectedAccountId=${selectedAccountId}${pagePathParam}`,
				dataTitle: labels.get('LOCATION_DETAILS')
			};
		}

		case 'CONTACT_US_FOR_DETAILS':
			return {
				...defaultLinkObj,
				linkLabel: labels.get('CONTACT_US_FOR_DETAILS'),
				dataWidth: 375,
				href: `${sitemap.get(
					'INVENTORY_LEAD_DEFAULT'
				)}?itemId=${vehicleId}&category=${vehicleCategory}&parentPageAlias=${pageAlias}`,
				dataHref: `${sitemap.get(
					'INVENTORY_LEAD_DEFAULT'
				)}?itemId=|itemId|&category=|category|&parentPageAlias=${pageAlias}`,
				dataTitle: labels.get('REQUEST_MORE_INFO'),
				relData: 'nofollow'
			};

		default:
			return {
				...defaultLinkObj,
				displayLink: false
			};
	}
};

/**
 * Receives location prop
 * returns object of location properties to render in widget
 * @param status
 * @param propLocation
 * @param labels
 */

const getStatus = (status, { name }, labels) => {
	const defaultStatusObj = {
		icon: 'map',
		vehicleStatusDisplay: labels.get('ON_THE_LOT_VDP'),
		location: `${labels.get('AT')} ${name}`,
		displayStatusClass: '',
		displayStatusElement: 'strong',
		locationElement: '',
		isInTransit: false
	};
	switch (status) {
		case 'IN_TRANSIT':
			return {
				...defaultStatusObj,
				icon: 'in-transit',
				vehicleStatusDisplay: labels.get('IN_TRANSIT'),
				location: `${labels.get('TO')} ${name}`,
				isInTransit: true
			};

		case 'RESERVED':
			return {
				...defaultStatusObj,
				icon: 'lock',
				vehicleStatusDisplay: labels.get('OSIRIS_RESERVED'),
				displayStatusClass: 'text-danger'
			};

		case 'OFF_LOT':
			return {
				...defaultStatusObj,
				icon: 'off-lot',
				vehicleStatusDisplay: labels.get('LOCATED_AT'),
				location: name,
				displayStatusElement: '',
				locationElement: 'strong'
			};

		case 'IN_TRANSIT_AT_PORT':
			return {
				...defaultStatusObj,
				icon: 'in-transit',
				vehicleStatusDisplay: labels.get('IN_TRANSIT_FROM_PORT'),
				location: `${labels.get('TO')} ${name}`,
				isInTransit: true
			};

		case 'IN_TRANSIT_AT_FACTORY':
			return {
				...defaultStatusObj,
				icon: 'in-transit',
				vehicleStatusDisplay: labels.get('IN_TRANSIT_FROM_FACTORY'),
				location: `${labels.get('TO')} ${name}`,
				isInTransit: true
			};
		case 'IN_TRANSIT_SOLD':
			return {
				...defaultStatusObj,
				icon: 'in-transit',
				vehicleStatusDisplay: labels.get('IN_TRANSIT_SOLD'),
				location: `${labels.get('TO')} ${name}`,
				isInTransit: true
			};

		default:
			return defaultStatusObj;
	}
};

/**
 * Receives location prop
 * returns object of text properties to render in widget
 * @param propLocation
 * @param linkOpt
 * @param labels
 * @param sitemap
 * @param overrideLocation
 * @param widgetInfo
 * @param prefs
 */
export function getLocationAttributes(
	propLocation,
	linkOpt,
	labels,
	sitemap,
	overrideLocation,
	widgetInfo,
	prefs = {},
	isLocationPopup
) {
	const { deviceType } = widgetInfo;
	const {
		status: locationStatus,
		vehicleLat,
		vehicleLong,
		vehicleZip,
		isOnPlatformAccount
	} = propLocation;
	let status = '';

	if (locationStatus) {
		if (
			overrideLocation === 'true' &&
			locationStatus.toUpperCase() === 'OFF_LOT'
		) {
			status = 'ON_LOT';
		} else {
			status = locationStatus;
		}
	}

	const vehicleLoc = {
		vehicleLat,
		vehicleLong,
		vehicleZip
	};

	const { linkToChildVDP, showWebSiteLocation } = prefs;
	const enableLinkToChildVDP =
		showWebSiteLocation === 'false' &&
		linkToChildVDP === 'true' &&
		isOnPlatformAccount;

	const linkObj = getLink(
		linkOpt,
		labels,
		sitemap,
		widgetInfo,
		enableLinkToChildVDP,
		isLocationPopup
	);
	const statusObj = getStatus(status.toUpperCase(), propLocation, labels);

	return {
		...statusObj,
		...vehicleLoc,
		...linkObj,
		isMobile: deviceType !== 'DESKTOP'
	};
}

/**
 * Create formatted search request object
 * @param {string} vehicleId
 * @returns {object} - Formatted search request object
 */
export const getSearchRO = (vehicleId) => ({
	vehicle: {
		uuid: vehicleId,
		select: ['category', 'status']
	}
});

export const getSiteAccountId = (requestData) => {
	return (
		(requestData.parameterMap && requestData.parameterMap.accountId) ||
		requestData.accountId
	);
};

const deg2rad = (deg) => {
	return deg * (Math.PI / 180);
};

const calculateDistance = (lat1, lon1, lat2, lon2) => {
	const R = 6371; // Radius of the earth in km
	const dLat = deg2rad(lat2 - lat1); // deg2rad below
	const dLon = deg2rad(lon2 - lon1);
	const a =
		Math.sin(dLat / 2) * Math.sin(dLat / 2) +
		Math.cos(deg2rad(lat1)) *
			Math.cos(deg2rad(lat2)) *
			Math.sin(dLon / 2) *
			Math.sin(dLon / 2);
	const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
	const d = R * c; // Distance in km
	return d;
};

export const getDistance = (
	userLat,
	userLong,
	vehicleLat,
	vehicleLong,
	locale
) => {
	const kilometers = calculateDistance(
		userLat,
		userLong,
		vehicleLat,
		vehicleLong
	);
	const miles = kilometers / 1.609;

	return locale === 'en_US' || locale === 'es_US' ? miles : kilometers;
};

/**
 * Validates if an offsite VDP URL is valid
 * @param {string|null|undefined} url - The URL to validate
 * @returns {boolean} - True if URL is valid, false otherwise
 */
export const isValidOffsiteUrl = (url) => {
	// Check if url is null, undefined, or empty string
	if (!url || typeof url !== 'string' || url.trim() === '') {
		return false;
	}

	// Trim whitespace
	const trimmedUrl = url.trim();

	// Check if it starts with http:// or https://
	if (
		!trimmedUrl.startsWith('http://') &&
		!trimmedUrl.startsWith('https://')
	) {
		return false;
	}

	// Try to create a valid URL object
	try {
		const urlObj = new URL(trimmedUrl);

		// Additional checks for valid URL structure
		if (!urlObj.hostname || urlObj.hostname.length < 3) {
			return false;
		}

		// Check if hostname has at least one dot (basic domain validation)
		if (!urlObj.hostname.includes('.')) {
			return false;
		}

		return true;
	} catch (error) {
		// If URL constructor throws an error, it's invalid
		return false;
	}
};

/**
 * Checks if offsite VDP URL should be used based on preferences and conditions
 * @param {string} showWebSiteLocation
 * @param {string} linkToChildVDP
 * @param {boolean} offSiteVDPFlag
 * @param {string|null} offsiteVdpUrl
 * @returns {boolean}
 */
export const shouldUseOffsiteVdpUrl = (
	showWebSiteLocation,
	linkToChildVDP,
	offSiteVDPFlag,
	offsiteVdpUrl
) => {
	return (
		showWebSiteLocation === 'false' &&
		linkToChildVDP === 'true' &&
		offSiteVDPFlag &&
		isValidOffsiteUrl(offsiteVdpUrl)
	);
};

export const getValidHttpsUrl = (url) => {
	if (!url || typeof url !== 'string' || url.trim() === '') {
		return '';
	}

	try {
		let parsedUrl = new URL(url);
		if (parsedUrl.protocol !== 'https:') {
			parsedUrl = new URL(
				`https://${parsedUrl.hostname}${parsedUrl.pathname}${parsedUrl.search}`
			);
		}

		return parsedUrl.href;
	} catch (error) {
		return `https://${url}`;
	}
};
